/* Phase 6 · KRA Leaderboard (director only) */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, Target } from "lucide-react";
import { KRAWeeklyRunButton } from "./AdminWidgets";

const toneBadge = {
  green: "bg-emerald-100 text-emerald-800 border-emerald-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  red:   "bg-red-100 text-red-800 border-red-200",
};

export default function KRALeaderboard() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/kra/leaderboard").then(r => setRows(r.data));
  }, []);

  const shown = filter ? rows.filter(r => r.role === filter) : rows;
  const roles = Array.from(new Set(rows.map(r => r.role)));

  return (
    <div className="p-4 md:p-6 max-w-5xl" data-testid="kra-leaderboard">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#7C8E9E]">ADMIN · PERFORMANCE</div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0B2340] flex items-center gap-2">
            <Trophy size={20} className="text-[#B08B49]" /> KRA Leaderboard
          </h1>
        </div>
        <KRAWeeklyRunButton />
      </div>

      <div className="bg-white p-3 rounded-lg border border-slate-100 mb-3">
        <div className="text-xs text-[#4A627A] mb-1">Filter by role</div>
        <div className="flex gap-1.5 flex-wrap">
          <button onClick={() => setFilter("")} data-testid="kra-filter-all"
            className={`px-3 py-1 rounded-full text-xs border ${filter === "" ? "bg-[#1B3A5C] text-white border-[#1B3A5C]" : "bg-white text-[#4A627A] border-slate-200"}`}>
            All
          </button>
          {roles.map(r => (
            <button key={r} onClick={() => setFilter(r)} data-testid={`kra-filter-${r}`}
              className={`px-3 py-1 rounded-full text-xs border ${filter === r ? "bg-[#1B3A5C] text-white border-[#1B3A5C]" : "bg-white text-[#4A627A] border-slate-200"}`}>
              {r}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-[#7C8E9E]">
            <tr>
              <th className="text-left py-2 px-3">Rank</th>
              <th className="text-left py-2 px-3">Name</th>
              <th className="text-left py-2 px-3">Role</th>
              <th className="text-right py-2 px-3">Score</th>
              <th className="text-left py-2 px-3">Top metric</th>
              <th className="text-left py-2 px-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row, i) => (
              <tr key={row.user_id} className="border-t border-slate-100 hover:bg-slate-50"
                data-testid={`kra-row-${row.username}`}>
                <td className="py-2 px-3 font-bold text-[#7C8E9E]">#{i + 1}</td>
                <td className="py-2 px-3 font-semibold text-[#0B2340]">{row.name}</td>
                <td className="py-2 px-3 text-xs text-[#4A627A]">{row.role}</td>
                <td className="py-2 px-3 text-right">
                  <span className="inline-flex items-center gap-1 text-lg font-bold text-[#0B2340]">
                    <Target size={12} className="text-[#B08B49]" /> {row.score}
                  </span>
                </td>
                <td className="py-2 px-3 text-xs text-[#4A627A]">
                  {row.top_metric ? `${row.top_metric.label}: ${row.top_metric.value}` : "—"}
                </td>
                <td className="py-2 px-3">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border ${toneBadge[row.tone]}`}>
                    {row.tone === "green" ? "On target" : row.tone === "amber" ? "Attention" : "Below target"}
                  </span>
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-[#7C8E9E]">No KRA rows yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
